import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/synapse/Icon';
import { ApiError } from '@/lib/api';
import { translateApiError } from '@/lib/apiError';
import { cn } from '@/lib/utils';
import type { Patient } from '@/types/patients';
import { usePatientSearch, useRecentPatients } from './usePatients';
import { PatientResultRow } from './PatientResultRow';

interface PatientListProps {
  query: string;
  selectedId?: string;
  onSelect: (p: Patient) => void;
  onAddNew: (prefill: string) => void;
  variant: 'card' | 'row';
  /**
   * Id of the row to render as keyboard-highlighted. Controlled by the parent
   * pane (which owns the keydown handler on the wrapper around the search input
   * + this list) so that keystrokes typed in the input drive the highlight.
   */
  highlightedId?: string | null;
  /**
   * Called whenever the visible, ordered result set changes so the parent can
   * drive ↑/↓/Enter against the same ordering the user sees.
   */
  onResultsChange?: (patients: Patient[]) => void;
}

const MIN_QUERY = 2;

/** Stable DOM id for a result row, used for `aria-activedescendant`. */
export function patientRowDomId(patientId: string): string {
  return `patient-row-${patientId}`;
}

/**
 * Search-as-you-type result list with the design's state machine:
 *   - trimmed query < 2 chars → recent patients
 *   - fetching                → skeleton cards
 *   - results                 → rows with match highlighting
 *   - zero results            → "Add '<query>' as new patient" affordance
 *
 * Keyboard navigation (↑/↓/Enter) is owned by the parent pane via a keydown
 * handler on the wrapper around the search input + this list; this component is
 * controlled — it renders `highlightedId` and reports its ordered results back
 * through `onResultsChange` so the parent can move the highlight and select.
 */
export function PatientList({
  query,
  selectedId,
  onSelect,
  onAddNew,
  variant,
  highlightedId,
  onResultsChange,
}: PatientListProps) {
  const { t } = useTranslation();
  const trimmed = query.trim();
  const isSearching = trimmed.length >= MIN_QUERY;

  const recent = useRecentPatients({ enabled: !isSearching });
  const search = usePatientSearch({ q: trimmed, enabled: isSearching });

  const active = isSearching ? search : recent;
  const patients: Patient[] = active.data ?? [];

  // Publish the ordered, currently-visible results to the parent so its keydown
  // handler navigates against exactly what the user sees. Settled-state only
  // (loading/error lists are empty) — the effect re-runs when the array ref or
  // length changes.
  const settledPatients = !active.isLoading && !active.isError ? patients : EMPTY;
  useEffect(() => {
    onResultsChange?.(settledPatients);
  }, [onResultsChange, settledPatients]);

  // ---- loading ----
  if (active.isLoading) {
    return (
      <div className="flex flex-col gap-2" aria-busy="true" aria-live="polite">
        <SectionLabel>{isSearching ? t('patients.searchingLabel') : t('patients.recentPatients')}</SectionLabel>
        {[0, 1, 2, 3].map((i) => (
          <SkeletonCard key={i} variant={variant} />
        ))}
      </div>
    );
  }

  // ---- error ----
  if (active.isError) {
    const msg =
      active.error instanceof ApiError ? translateApiError(active.error) : t('patients.couldNotLoadMsg');
    return (
      <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive-bg text-destructive">
          <Icon name="alert" size={22} />
        </div>
        <div className="text-[14px] font-semibold tracking-[-0.01em]">{t('patients.couldNotLoad')}</div>
        <p className="max-w-[320px] text-[12.5px] text-muted">{msg}</p>
        <Button onClick={() => active.refetch()}>{t('common.tryAgain')}</Button>
      </div>
    );
  }

  // ---- zero results while searching ----
  if (isSearching && patients.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 px-6 py-12 text-center">
        <div className="flex h-[52px] w-[52px] items-center justify-center rounded-full bg-paper-3 text-muted-2">
          <Icon name="search" size={22} />
        </div>
        <div>
          <div className="text-[15.5px] font-semibold tracking-[-0.01em]">{t('patients.noResultsTitle')}</div>
          <p className="mx-auto mt-1.5 max-w-[280px] text-[13px] text-muted">
            {t('patients.noResultsBody', { query: trimmed })}
          </p>
        </div>
        <Button size="lg" onClick={() => onAddNew(trimmed)}>
          <Icon name="plus" size={16} /> {t('patients.addAsNew', { query: trimmed })}
        </Button>
      </div>
    );
  }

  // ---- recent, empty ("no patients yet") ----
  if (!isSearching && patients.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 px-6 py-12 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-paper-2 text-muted">
          <Icon name="user" size={22} />
        </div>
        <div>
          <div className="text-[14px] font-semibold tracking-[-0.01em]">{t('patients.noRecentTitle')}</div>
          <p className="mx-auto mt-1.5 max-w-[320px] text-[12.5px] text-muted">{t('patients.noRecentBody')}</p>
        </div>
        {/* Default call-to-action so a clinic with no patients yet can start one
            straight from the list (no query to prefill). */}
        <Button size="lg" onClick={() => onAddNew('')}>
          <Icon name="plus" size={16} /> {t('patients.newPatient')}
        </Button>
      </div>
    );
  }

  // ---- results / recent list ----
  const header = isSearching
    ? t('patients.resultsCount', { count: patients.length, query: trimmed })
    : t('patients.recentPatients');

  return (
    // Keydown is handled by the parent wrapper (which also holds the search
    // input), so keystrokes from the focused input drive this list's highlight.
    <div id="patient-results" role="listbox" aria-label={header}>
      <SectionLabel>{header}</SectionLabel>
      <div className={cn(variant === 'card' ? 'flex flex-col gap-2' : 'divide-y divide-line')}>
        {patients.map((p) => (
          <div
            key={p.id}
            id={patientRowDomId(p.id)}
            role="option"
            aria-selected={p.id === selectedId || p.id === highlightedId}
          >
            <PatientResultRow
              patient={p}
              query={isSearching ? trimmed : ''}
              variant={variant}
              active={p.id === selectedId || p.id === highlightedId}
              onSelect={() => onSelect(p)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Shared stable empty array so `onResultsChange` doesn't fire on every render. */
const EMPTY: Patient[] = [];

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-0.5 pb-2 pt-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-2">
      {children}
    </div>
  );
}

function SkeletonCard({ variant }: { variant: 'card' | 'row' }) {
  return (
    <div
      className={cn(
        'flex items-center gap-3',
        variant === 'card' ? 'rounded-md border border-line bg-paper p-3' : 'px-4 py-3',
      )}
    >
      <div className="h-11 w-11 shrink-0 animate-pulse rounded-full bg-paper-3" />
      <div className="flex-1">
        <div className="mb-2 h-3 w-1/2 animate-pulse rounded bg-paper-3" />
        <div className="mb-1.5 h-2.5 w-3/4 animate-pulse rounded bg-paper-3" />
        <div className="h-2.5 w-2/5 animate-pulse rounded bg-paper-3" />
      </div>
    </div>
  );
}
